<template>
  <div class="flex min-h-full flex-col bg-white dark:bg-slate-900">

    <!-- ── HEADER: search + meta ── -->
    <div class="border-b border-gray-200 dark:border-slate-700 px-4 py-4">
      <!-- Search input — same style as Calls page -->
      <div class="relative mb-3">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Search contacts…"
          class="block w-full rounded-xl bg-gray-100 dark:bg-slate-800 py-2.5 pl-10 pr-3 text-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary"
        >
      </div>

      <!-- Meta row: contact count + sync status -->
      <div class="flex items-center justify-between">
        <span class="text-xs text-gray-400">
          <template v-if="isReady">
            {{ contactStore.contacts.length.toLocaleString() }} contact{{ contactStore.contacts.length !== 1 ? 's' : '' }}
          </template>
        </span>
        <NuxtLink
          to="/linked-devices"
          class="flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition hover:bg-gray-50 dark:hover:bg-slate-800"
          :class="syncBadgeBorderClass"
          :title="syncBadgeTitle"
        >
          <span class="h-1.5 w-1.5 rounded-full" :class="syncDotClass" />
          {{ syncBadgeLabel }}
        </NuxtLink>
      </div>
    </div>

    <!-- Loading skeleton — shown while reading from the local IndexedDB cache -->
    <template v-if="!isReady">
      <div v-for="i in 10" :key="i" class="flex items-center gap-3 border-b border-gray-100 dark:border-slate-800 px-4 py-3">
        <div class="h-10 w-10 shrink-0 animate-pulse rounded-full bg-gray-200" />
        <div class="flex-1 space-y-2">
          <div class="h-3.5 w-36 animate-pulse rounded-full bg-gray-200" />
          <div class="h-3 w-24 animate-pulse rounded-full bg-gray-100 dark:bg-slate-800" />
        </div>
      </div>
    </template>

    <!-- ── UNPAIRED EMPTY STATE — only shown after init completes ── -->
    <div
      v-else-if="contactStore.status === 'unpaired' && contactStore.contacts.length === 0"
      class="flex flex-1 flex-col items-center justify-center gap-6 py-20 text-center"
    >
      <div class="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800">
        <svg class="h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>
      <div>
        <h2 class="text-lg font-semibold text-gray-900 dark:text-slate-100">No contacts yet</h2>
        <p class="mx-auto mt-1.5 max-w-xs text-sm text-gray-500 dark:text-slate-400">
          Sync your phone contacts to see them here. They're encrypted end-to-end and only visible to you.
        </p>
      </div>
      <NuxtLink
        to="/linked-devices"
        class="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        Sync from Mobile App
      </NuxtLink>
    </div>

    <template v-else>

      <!-- No search results -->
      <div
        v-if="filteredContacts.length === 0"
        class="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center"
      >
        <div class="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800">
          <svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-gray-900 dark:text-slate-100">No results for "{{ searchQuery }}"</p>
          <p class="mt-1 text-xs text-gray-400">Try a different name or phone number.</p>
        </div>
      </div>

      <!-- Grouped alphabetical list -->
      <template v-else>
        <template v-for="group in groupedContacts" :key="group.letter">
          <!-- Section letter header -->
          <div class="border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 px-4 py-1.5">
            <span class="text-xs font-semibold uppercase tracking-wider text-gray-400">{{ group.letter }}</span>
          </div>

          <!-- Contacts in this group -->
          <ul>
            <li
              v-for="contact in group.contacts"
              :key="contact.id"
              class="group flex items-center gap-3 border-b border-gray-100 dark:border-slate-800/80 px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800 last:border-0"
            >
              <!-- Colour-coded avatar with up-to-two-letter initials -->
              <div
                class="flex h-10 w-10 shrink-0 select-none items-center justify-center rounded-full text-sm font-semibold uppercase"
                :class="avatarColor(contact.displayName)"
              >
                {{ initials(contact.displayName) }}
              </div>

              <!-- Name + phone -->
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-medium text-gray-900 dark:text-slate-100">{{ contact.displayName }}</p>
                <p v-if="contact.phones[0]" class="mt-0.5 truncate text-xs text-gray-400">
                  <span v-if="contact.phones[0].label" class="mr-1 font-medium text-gray-300">{{ contact.phones[0].label }}</span>{{ contact.phones[0].number }}
                </p>
              </div>

              <!-- Call action (visible on row hover) -->
              <button
                v-if="contact.phones[0]"
                class="ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-600 opacity-0 transition-all hover:bg-green-100 group-hover:opacity-100"
                :aria-label="`Call ${contact.displayName}`"
                @click="callContact(contact)"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </button>
            </li>
          </ul>
        </template>
      </template>

    </template>

    <!-- Call-from-mobile dialog (reuses the same component as the Calls page) -->
    <CallMobileOnlyDialog
      :show="!!callTarget"
      :call="callTarget"
      @close="callTarget = null"
    />
  </div>
</template>

<script setup lang="ts">
import type { Database } from 'firebase/database';
import type { SyncedContact } from '~/types/contacts';
import type { CallRecord } from '~/types/calls';
import { useContactStore } from '~/stores/contactStore';
import { useNavbarInfo } from '~/stores/useNabvarInfo';
import CallMobileOnlyDialog from '~/components/calls/CallMobileOnlyDialog.vue';

definePageMeta({ ssr: false });

const navbar = useNavbarInfo();
navbar.changeInfo('Contacts', []);

useHead({ title: 'Contacts' });

// ── Auth & Firebase ───────────────────────────────────────────────────────────

const { $auth, $rtdb } = useNuxtApp();
const db = $rtdb as unknown as Database;
const uid = ($auth as ReturnType<typeof import('firebase/auth').getAuth> | undefined)?.currentUser?.uid;

// ── Store ─────────────────────────────────────────────────────────────────────

const contactStore = useContactStore();
const isReady = ref(false);
const searchQuery = ref('');
const callTarget = ref<CallRecord | null>(null);

onMounted(async () => {
  if (!uid) return;
  await contactStore.initialize(uid, db);
  isReady.value = true;
});

// ── Derived ───────────────────────────────────────────────────────────────────

const filteredContacts = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return contactStore.contacts;
  return contactStore.contacts.filter(
    (c) =>
      c.displayName.toLowerCase().includes(q) ||
      c.phones.some((p) => p.number.includes(q) || p.normalizedNumber.includes(q)),
  );
});

// Contacts grouped by first letter (# bucket for non-alpha names, sorted last).
const groupedContacts = computed(() => {
  const groups = new Map<string, SyncedContact[]>();
  for (const contact of filteredContacts.value) {
    const first = contact.displayName.trim()[0] ?? '';
    const letter = /[a-zA-Z]/.test(first) ? first.toUpperCase() : '#';
    if (!groups.has(letter)) groups.set(letter, []);
    groups.get(letter)!.push(contact);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)))
    .map(([letter, contacts]) => ({ letter, contacts }));
});

// ── Sync badge ────────────────────────────────────────────────────────────────

const syncBadgeLabel = computed(() => {
  if (contactStore.status === 'syncing') return 'Syncing…';
  if (contactStore.status === 'paired') return 'Synced';
  if (contactStore.status === 'error') return 'Sync error';
  return 'Not connected';
});

const syncBadgeTitle = computed(() =>
  contactStore.status === 'paired' && contactStore.lastSyncedAt
    ? `Last synced ${new Date(contactStore.lastSyncedAt).toLocaleTimeString()}`
    : 'Manage linked devices',
);

const syncDotClass = computed(() => ({
  'bg-green-500': contactStore.status === 'paired',
  'bg-primary animate-pulse': contactStore.status === 'syncing',
  'bg-red-500': contactStore.status === 'error',
  'bg-gray-400': contactStore.status === 'unpaired',
}));

const syncBadgeBorderClass = computed(() => ({
  'border-green-200 text-green-700': contactStore.status === 'paired',
  'border-blue-200 text-blue-600': contactStore.status === 'syncing',
  'border-red-200 text-red-600': contactStore.status === 'error',
  'border-gray-200 dark:border-slate-700 text-gray-500 dark:text-slate-400': contactStore.status === 'unpaired',
}));

// ── Avatar helpers ────────────────────────────────────────────────────────────

// 8-colour palette — deterministic per first character so the same contact
// always gets the same colour across renders.
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-violet-100 text-violet-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
  'bg-orange-100 text-orange-700',
  'bg-green-100 text-green-700',
];

function avatarColor(name: string): string {
  const code = name.trim().toLowerCase().charCodeAt(0) || 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// Up to two initials: first letter of first + last name word.
function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '#';
  if (!/[a-zA-Z]/.test(trimmed[0])) return '#';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2 && /[a-zA-Z]/.test(parts[parts.length - 1][0])) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0][0].toUpperCase();
}

// ── Actions ───────────────────────────────────────────────────────────────────

// Opens the "call from mobile" dialog with a minimal outbound CallRecord built
// from the contact's primary phone number.
function callContact(contact: SyncedContact) {
  const phone = contact.phones[0];
  if (!phone) return;
  callTarget.value = {
    id: contact.id,
    bpCallDirection: 'outbound',
    callType: null,
    callerId: null,
    callerNumber: '',
    destinationNumber: phone.normalizedNumber || phone.number,
    startEpoch: 0,
    talkTimeSec: null,
    billSec: null,
    hangupCause: null,
    hangupCauseReason: null,
    did: null,
  };
}
</script>

